import { Suspense, lazy } from 'react'

import type { RouteHistory } from './types'
import { useLiveRoutePoints } from './useLiveRoutePoints'

// Loaded on first render so Mapbox stays out of the bundles that import this: the insight
// registry and the asset map's detail sheet.
const RouteMap = lazy(() => import('./RouteMap').then((m) => ({ default: m.RouteMap })))

export const MapSkeleton = () => <div className="skeleton h-full w-full min-h-64 rounded-lg" />

/**
 * The route map, following the route live while the asset is reporting. Shared by the
 * Route History insight and the asset detail's Route Map tab.
 *
 * `id` names this map's live route for the summary to follow (see liveRouteStore); `query`
 * is the route-history request query the live polls repeat.
 */
export const LiveRouteMap = ({
  id,
  data,
  query,
  companyId,
}: {
  id: string
  data: RouteHistory
  query: string
  /** For the geofences drawn under the route. */
  companyId?: number | string
}) => {
  const { route, live } = useLiveRoutePoints(id, data, query)
  return (
    <Suspense fallback={<MapSkeleton />}>
      <RouteMap data={route} companyId={companyId} live={live} />
    </Suspense>
  )
}

/**
 * Distinct places among the fixes. A parked unit keeps reporting from one spot, so its many
 * fixes draw as a single dot; counting places tells that apart from a short trip. Fixes are
 * compared at 4 decimal places (about 11 m) so GPS jitter while parked doesn't count as
 * movement.
 */
export const uniquePositions = (points: { latitude: number; longitude: number }[]): number =>
  new Set(points.map((p) => `${p.latitude.toFixed(4)},${p.longitude.toFixed(4)}`)).size

const plural = (n: number, word: string) => `${String(n)} ${word}${n === 1 ? '' : 's'}`

/** "151 locations (1 unique) · 0 door openings · 2 alerts" */
export const RouteSummary = ({ route }: { route: RouteHistory }) => (
  <div>
    {plural(route.points.length, 'location')} ({uniquePositions(route.points)} unique) ·{' '}
    {plural(route.doorOpens.length, 'door opening')} · {plural(route.alerts.length, 'alert')}
  </div>
)
